import {useMemo} from "react";


// Function to perform a true fuzzy match
// You can adjust the threshold as needed
export const fuzzyMatch = (text, pattern, threshold = .50) => {
    if (!pattern || pattern.trim() === '') return true;
    if (!text) return false;

    const cleanText = text.toLowerCase();
    const cleanPattern = pattern.toLowerCase();

    if (cleanPattern.length <= 3) {
        return cleanText.includes(cleanPattern);
    }

    const checkDistance = (str1, str2) => {
        const track = Array(str2.length + 1).fill(null).map(() =>
            Array(str1.length + 1).fill(null));

        for (let i = 0; i <= str1.length; i++) {
            track[0][i] = i;
        }

        for (let j = 0; j <= str2.length; j++) {
            track[j][0] = j;
        }

        for (let j = 1; j <= str2.length; j++) {
            for (let i = 1; i <= str1.length; i++) {
                const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
                track[j][i] = Math.min(
                    track[j][i - 1] + 1, // deletion
                    track[j - 1][i] + 1, // insertion
                    track[j - 1][i - 1] + indicator // substitution
                );
            }
        }

        return track[str2.length][str1.length];
    };

    // Check if the pattern is a substring after normalizing
    if (cleanText.includes(cleanPattern)) {
        return true;
    }

    // For longer text, use a sliding window approach to find best match
    if (cleanText.length > cleanPattern.length * 3) {
        let bestScore = Infinity;

        // Find the best matching segment
        for (let i = 0; i <= cleanText.length - cleanPattern.length; i++) {
            const segment = cleanText.substring(i, i + cleanPattern.length * 2);
            const distance = checkDistance(segment, cleanPattern);
            bestScore = Math.min(bestScore, distance);
        }

        // Calculate similarity as a ratio of match quality
        const maxPossibleDistance = cleanPattern.length;
        const similarity = 1 - (bestScore / maxPossibleDistance);

        return similarity >= threshold;
    }

    // For shorter texts, compare directly
    const distance = checkDistance(cleanText, cleanPattern);
    const maxPossibleDistance = Math.max(cleanText.length, cleanPattern.length);
    const similarity = 1 - (distance / maxPossibleDistance);

    return similarity >= threshold;
};

export const useEntityFiltering = (
    associatedEntities,
    filterText,
    showNested,
    entity
) => {
    return useMemo(() => {
        if (!associatedEntities) return {};
        // Create a base result based on the showNested option
        let result = {};
        // If showNested is true, include all entities
        // If showNested is false, only include direct children
        if (showNested) {
            result = {...associatedEntities};
        } else {
            // Only include sections that directly relate to the current entity
            Object.entries(associatedEntities).forEach(([title, entityData]) => {
                if (entity?.name === title || entity?.entityid === title || entity?.entityID === title) {
                    result[title] = entityData;
                }
            });
        }

        // Apply fuzzy filtering if filter text exists
        if (filterText.trim() !== '') {
            const filteredResult = {};

            Object.entries(result).forEach(([title, entityData]) => {
                // Filter entities that match the search text
                const filteredChildren = entityData.children.filter(entity => {
                    // Check if any of the entity properties match the filter
                    return fuzzyMatch(entity?.name || '', filterText) ||
                        fuzzyMatch(entity?.entityid || '', filterText) ||
                        fuzzyMatch(entity?.details?.description || '', filterText);
                });

                // Only include sections with matching entities
                if (filteredChildren.length > 0) {
                    filteredResult[title] = {
                        entityID: entityData.entityID,
                        children: filteredChildren
                    };
                }
            });
            return filteredResult;
        }
        return result;
    }, [associatedEntities, showNested, entity, filterText]);
};